import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store';

import Index from './views/Index.vue';
import Nested from './views/helpers/Nested.vue';
import Profile from './views/Profile.vue';

import TasksList from './views/TasksList.vue';

import DocsList from './views/DocsList.vue';
import DocsView from './views/DocsView.vue';
import DocsViewRead from './views/DocsViewRead.vue';
import DocsViewComments from './views/DocsViewComments.vue';
import DocsViewEdit from './views/DocsViewEdit.vue';
import DocsViewTask from './views/DocsViewTask.vue';

import Site from './views/Site.vue';
import SiteTests from './views/SiteTests.vue';
import SiteLessonsIndex from './views/SiteLessonsIndex.vue';
import SiteLessonsRead from './views/SiteLessonsRead.vue';

import Control from './views/Control.vue';
import ControlUsers from './views/ControlUsers.vue';
import ControlUsersList from './views/ControlUsersList.vue';
import ControlUsersEdit from './views/ControlUsersEdit.vue';
import ControlTestsList from './views/ControlTestsList.vue';
import ControlTestsEdit from './views/ControlTestsEdit.vue';
import ControlSettingsCategories from '@/views/ControlSettingsCategories';
import ControlSettingsUserGroups from '@/views/ControlSettingsUserGroups';
import ControlLessons from './views/ControlLessons.vue';
import ControlLessonsEdit from './views/ControlLessonsEdit.vue';
import ControlCategoryEdit from './views/ControlCategoryEdit';
import ControlSettings from './views/ControlSettings.vue';
import ControlReports from '@/views/ControlReports';
import ControlReportsLessons from '@/views/ControlReportsLessons';
import ControlDetailedReportsLesson from '@/views/ControlDetailedReportsLesson';
import ControlReportsTests from '@/views/ControlReportsTests';
import ControlDetailedReportsTest from '@/views/ControlDetailedReportsTest';
import ControlMessages from '@/views/ControlMessages';
import ControlArbitraryAnswers from '@/views/ControlArbitraryAnswers';

Vue.use(Router);

const route = (name, path, component, optionsOrTitle = {}) => {
  let options =
    typeof optionsOrTitle == 'string' ? { meta: { title: optionsOrTitle } } : optionsOrTitle;

  return {
    name,
    path,
    component,
    props: true,
    ...options,
  };
};

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  linkActiveClass: 'is-active',
  linkExactActiveClass: 'is-active',

  routes: [
    // =========================================================================
    // Общие разделы
    // =========================================================================
    route('index', '/', Index),

    // Профиль пользователя
    route('profile', '/profile', Profile, 'Настройки профиля'),

    // Задачи (План)
    route('tasks', '/tasks', Nested, {
      redirect: { name: 'tasks.list' },
      meta: { title: 'План задач' },
      children: [route('tasks.list', 'list', TasksList)],
    }),

    // База знаний
    route('docs', '/docs', Nested, {
      redirect: { name: 'docs.list' },
      meta: { title: 'База знаний' },
      children: [
        route('docs.list', 'list', DocsList),
        route('docs.create', 'new', DocsViewEdit, 'Новый документ'),
        route('docs.view', ':id', DocsView, {
          redirect: { name: 'docs.view.read' },
          meta: { title: 'Просмотр' },
          children: [
            route('docs.view.read', 'read', DocsViewRead),
            route('docs.view.comments', 'comments', DocsViewComments, 'Комментарии'),
            route('docs.view.edit', 'edit', DocsViewEdit, 'Редактировать'),
            route('docs.view.task', 'task', DocsViewTask, 'Доступ'),
          ],
        }),
      ],
    }),

    route('app', '/app', Site, {
      children: [route('app.tests', 'tests', SiteTests, 'Тесты')],
    }),

    route('site', '/site', Site, {
      children: [
        route('site.lessons', 'lessons', Nested, {
          redirect: { name: 'site.lessons.index' },
          children: [
            route('site.lessons.index', 'list', SiteLessonsIndex),
            route('site.lessons.read', ':id/read', SiteLessonsRead),
          ],
        }),

        route('site.tests', 'tests', SiteTests),
      ],
    }),

    // =========================================================================
    // CONTROL (ADMIN SECTION)
    // =========================================================================
    route('control', '/control', Control, {
      children: [
        // Управление Пользователями
        route('control.users', 'users', ControlUsers, {
          redirect: { name: 'control.users.list' },
          meta: { title: 'Управление пользователями' },
          children: [
            route('control.users.list', 'group-:groupId?', ControlUsersList),
            route(
              'control.users.create',
              'group-:groupId?/user-new',
              ControlUsersEdit,
              'Новый пользователь'
            ),
            route(
              'control.users.edit',
              'group-:groupId/user-:userId/edit',
              ControlUsersEdit,
              'Редактирование профиля'
            ),
          ],
        }),

        // Настройки
        route('control.settings', 'settings', ControlSettings, {
          redirect: { name: 'control.settings.categories' },
          children: [
            route(
              'control.settings.categories',
              'categories',
              ControlSettingsCategories,
              'Категории материалов'
            ),
            route(
              'control.settings.user-groups',
              'user-groups',
              ControlSettingsUserGroups,
              'Группы пользователей'
            ),
          ],
        }),

        // Управление Тестами
        route('control.tests', 'tests', Nested, {
          meta: { title: 'Управление тестами' },
          redirect: { name: 'control.tests.list' },
          children: [
            route('control.tests.list', 'list', ControlTestsList),
            route('control.tests.create', 'new', ControlTestsEdit),
            route('control.tests.edit', 'edit/:id', ControlTestsEdit, 'Редактирование теста'),
          ],
        }),

        route('control.lessons', 'lessons', ControlLessons),
        route('control.lessons.edit', 'lessons/edit/:id', ControlLessonsEdit),

        route('control.category.edit', 'category/edit/:id', ControlCategoryEdit),

        route('control.reports', 'reports', ControlReports, {
          redirect: { name: 'control.reports.tests' },
          children: [
            route('control.reports.lessons', 'lessons', ControlReportsLessons),
            route('control.reports.tests', 'tests', ControlReportsTests),
            route('control.reports.lesson', 'lessons/:lessonId', ControlDetailedReportsLesson),
            route(
              'control.reports.detailed-lesson',
              'lessons/:lessonId/users/:userId',
              ControlDetailedReportsLesson
            ),
            route('control.reports.test', 'tests/:testId', ControlDetailedReportsTest),
            route(
              'control.reports.detailed-test',
              'tests/:testId/users/:userId',
              ControlDetailedReportsTest
            ),
          ],
        }),

        route('control.messages', 'messages', ControlMessages),

        route('control.answers.arbitrary', 'answers/arbitrary', ControlArbitraryAnswers),
      ],
    }),
  ],
});

router.afterEach(to => {
  store.dispatch('loadBreadcrumbs', { routes: to.matched, params: to.params });
});

export default router;
